import { createAsyncThunk } from '@reduxjs/toolkit';

import { IUser } from '../UserSlice';
import {
  AdminApiPaths,
  APIDataProvider,
  ApiPaths,
  IGetPasswordToken,
  TApiResponse,
} from '../../api';
import AuthServiceHelper from '../../api/AuthServiceHelper';

export type TUserSignUp = {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  timezone: string;
  curriculum: string;
  grade: string;
  examDate?: number;
  clientId: string;
  clientSecret: string;
};

export type TSaveFcmToken = {
  token: string;
};

export type TUserSignUpRes = {
  // eslint-disable-next-line camelcase
  access_token: string;
  oauthUser: IUser;
};

export type TLogInAsUserRes = {
  user: IUser;
  userMapper: IUser[];
};

export type TUserForgotPassword = {
  email: string;
};

export type TUserForgotPasswordRes = {
  email: string;
};

export type TUserResetPassword = {
  email: string;
  code: string;
  password: string;
};

const apiDataProvider = new APIDataProvider();

export const userSignUp = createAsyncThunk(
  '/sign-up',
  async (data: TUserSignUp) => {
    const response = await apiDataProvider.post<TApiResponse<TUserSignUpRes>>(
      ApiPaths.signUp,
      data
    );

    return response.data;
  }
);

export const userSignIn = createAsyncThunk(
  '/sign-in',
  async (data: IGetPasswordToken) => {
    return AuthServiceHelper.getPasswordToken(data);
  }
);

export const getClientCredentialsTokenV2 = createAsyncThunk(
  '/client-credential',
  async (d) => {
    return AuthServiceHelper.getClientCredentialToken();
  }
);

export const saveFcmToken = createAsyncThunk(
  '/fcm-token/save',
  async (data: TSaveFcmToken) => {
    return apiDataProvider.post(ApiPaths.fcmToken, data);
  }
);

export const deleteFcmToken = createAsyncThunk(
  '/fcm-token/delete',
  async (token: string) => {
    return apiDataProvider.delete(`${ApiPaths.fcmToken}/${token}`);
  }
);

export const getAuthUser = createAsyncThunk('/user/authUser/get', async () => {
  const path = `${ApiPaths.user}/authUser`;

  const response = await apiDataProvider.get<TApiResponse<IUser>>(path);

  return response.data;
});

export const logInAsUser = createAsyncThunk(
  '/user/authUser/logInAsUser',
  async (userId: string) => {
    const path = `${AdminApiPaths.users}/log-in-as-user`;

    const response = await apiDataProvider.post<TApiResponse<TLogInAsUserRes>>(
      path,
      {
        userId,
      }
    );

    return response.data;
  }
);

export const userForgotPassword = createAsyncThunk(
  '/forgot-password',
  async (data: TUserForgotPassword) => {
    const response = await apiDataProvider.post<
      TApiResponse<TUserForgotPasswordRes>
    >(`${ApiPaths.auth}/forgot-password`, data);

    return response.data;
  }
);

export const userResetPassword = createAsyncThunk(
  '/reset-password',
  async (data: TUserResetPassword) => {
    const response = await apiDataProvider.post<TApiResponse<TUserSignUpRes>>(
      `${ApiPaths.auth}/reset-password`,
      data
    );

    return response.data;
  }
);
